import { FC, useMemo } from "react";
import { useContext } from "react";

import Loader from "../../../components/Loader";
import Text from "../../../components/Text";
import Options from "../../../components/Table/Options";
import { AuthContext } from "../../../contexts/AuthContext";
import { Payout } from "../../../types/Payout";
import { Option } from "../../../types/Option";
import { MASKED_PERFORMANCE_VALUE } from "../../../utils/tradesPerfKpisCookie";
import { PayoutModalMode } from "../PayoutModal";

import styles from "./styles.module.scss";

interface Props {
    isLoading: boolean;
    payouts: Payout[];
    handlePayoutModalOpen: (mode: PayoutModalMode, payout?: Payout) => void;
    handleConfirmOpen: (payout: Payout) => void;
    isOwnDashboard: boolean;
    hidePerformance: boolean;
    /** Contenu affiché en haut à droite du titre (ex. bouton "Ajouter un payout") */
    headerAction?: React.ReactNode;
}

const MONTHS_FR = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const PayoutTimeline: FC<Props> = ({
    payouts,
    isLoading,
    isOwnDashboard,
    hidePerformance,
    handlePayoutModalOpen,
    handleConfirmOpen,
    headerAction,
}) => {
    const { hasPermission } = useContext(AuthContext)!;

    const getOptions = (payout: Payout): Option[] => {
        const createdAt = new Date(payout.createdAt!);
        const isWithinEditWindow =
            createdAt > new Date(Date.now() - 15 * 24 * 60 * 60 * 1000);

        const options: Option[] = [];

        if (
            (isOwnDashboard &&
                isWithinEditWindow &&
                hasPermission("payout", "update")) ||
            hasPermission("admin-payouts", "update")
        ) {
            options.push({
                label: "Editer",
                onClick: () => handlePayoutModalOpen("edit", payout),
            });
        }

        if (
            hasPermission("payout", "delete") ||
            hasPermission("admin-payouts", "delete")
        ) {
            options.push({
                label: "Supprimer",
                onClick: () => handleConfirmOpen(payout),
            });
        }

        return options;
    };

    const groupedByMonth = useMemo(() => {
        const groups: Record<string, Payout[]> = {};
        const sorted = [...payouts].sort(
            (a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime()
        );
        for (const p of sorted) {
            const d = new Date(p.issueDate);
            const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(p);
        }
        return groups;
    }, [payouts]);

    const monthEntries = useMemo(() => {
        return Object.entries(groupedByMonth).sort(([a], [b]) => b.localeCompare(a));
    }, [groupedByMonth]);

    const formatDate = (issueDate: string) => {
        const d = new Date(issueDate);
        return d.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    const formatAmount = (amount: number) => {
        const sign = amount >= 0 ? "+" : "";
        return `${sign} ${new Intl.NumberFormat("fr-FR", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(amount)} $`;
    };
    const displayAmount = (amount: number) =>
        hidePerformance ? MASKED_PERFORMANCE_VALUE : formatAmount(amount);

    const formatMonthLabel = (key: string) => {
        const [y, m] = key.split("-").map(Number);
        return `${MONTHS_FR[m]} ${y}`;
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.tableau}>
                    <div className={styles.loaderWrap}>
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    if (payouts.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.tableau}>
                    <div className={styles.timelineHeader}>
                        <h2 className={styles.timelineTitle}>
                            📈 Historique des payouts
                        </h2>
                        {headerAction}
                    </div>
                    <div className={styles.titleSeparator} />
                    <div className={styles.noData}>
                        <Text variant="subtitle">
                            Aucun payout déclaré
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.tableau}>
                <div className={styles.timelineHeader}>
                    <h2 className={styles.timelineTitle}>
                        📈 Historique des payouts
                    </h2>
                    {headerAction}
                </div>
                <div className={styles.titleSeparator} />
            <div className={styles.timelineScroll}>
            <div className={styles.timeline}>
                {monthEntries.map(([monthKey, monthPayouts]) => {
                    const monthTotal = monthPayouts.reduce(
                        (acc, p) => acc + p.amount,
                        0
                    );
                    return (
                    <div key={monthKey} className={styles.monthBlock}>
                        <div className={styles.monthTitle}>
                            <span>📅 {formatMonthLabel(monthKey)}</span>
                            <span
                                className={`${styles.monthCumul} ${
                                    hidePerformance
                                        ? ""
                                        : monthTotal >= 0
                                          ? styles.monthCumulPositive
                                          : styles.monthCumulNegative
                                }`.trim()}
                            >
                                {displayAmount(monthTotal)}
                            </span>
                        </div>
                        <div className={styles.monthTitleSeparator} />
                        <div className={styles.monthTrack}>
                            <div className={styles.axis} />
                            {monthPayouts.map((payout) => {
                                const options = getOptions(payout);
                                return (
                                    <div
                                        key={payout.id}
                                        className={styles.timelineRow}
                                    >
                                        <div className={styles.timelineColumn}>
                                            <div className={styles.dot} />
                                            <div className={styles.connector} />
                                        </div>
                                        <div className={styles.cardColumn}>
                                            <div className={styles.rowContent}>
                                                <span className={styles.rowDate}>
                                                    {formatDate(payout.issueDate)}
                                                </span>
                                                <span
                                                    className={`${styles.rowAmount} ${
                                                        hidePerformance
                                                            ? ""
                                                            : payout.amount >= 0
                                                            ? styles.rowAmountPositive
                                                            : styles.rowAmountNegative
                                                    }`}
                                                >
                                                    {displayAmount(payout.amount)}
                                                </span>
                                                {options.length > 0 && (
                                                    <div
                                                        className={styles.rowActions}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Options
                                                            dropdownDirection="bottom-right"
                                                            options={options}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    );
                })}
            </div>
            </div>
            </div>
        </div>
    );
};

export default PayoutTimeline;
